// src/pages/Accounts/ExpenseEntry.js
import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Typography,
  Space,
  App,
  Select,
  Grid,
  Flex,
  message,
} from "antd";
import { DeleteOutlined} from "@ant-design/icons";
import InputNumberField from "../../components/Input/InputNumber";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  fetchExpenseCode,
  fetchExpenses,
  saveExpense,
  fetchHeadAccs,
} from "./expenseThunk";
import {
  fetchTagGroups,
  fetchTagGroupFields,
  fetchEmployees,
  fetchAccountsByType,
} from "../../store/commonThunks/lookups";
import SearchListAutoComplete from "../../components/SearchListAutoComplete";
import { useNavigate, useParams } from "react-router-dom";
import {
  dateTimeFormat,
  formatToUtc,
  getAccessType,
  getDateTimeFromSelectedDate,
} from "../../lib/functions";
import {
  setEntryDate,
  setIsEditExpense,
  setSelectedExpense,
  setRefreshList,
  setExpenseCode,
} from "./expenseSlice";
import AddNewModal from "../../components/DropDown/AddNewModal";
import TagCreateForm from "../Settings/Tags/components/TagCreateForm";
import { selectAuthInfo } from "../Auth/authSelectors";
import useMigrationDate from "../../hooks/useMigrationDate";

const { Title } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

const ExpenseEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { modal } = App.useApp();
  const [form] = Form.useForm();

  const [rows, setRows] = useState([]);
  const [selectedPayFromAcc, setSelectedPayFromAcc] = useState(null);
  const [selectedEmployeeLocal, setSelectedEmployeeLocal] = useState(null);
  const [tagGroupList, setTagGroupList] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagModal, setTagModal] = useState(false);
  let [access_type, access_type_set] = useState("");

  const {
    entryDate,
    expenseCode,
    headAccs,
    accountBalances,
    selectedExpense,
    isEditExpense,
  } = useSelector((state) => state.expenses);
  const { employees, accountsByType } = useSelector((s) => s.lookups);

  const authInfo = useSelector(selectAuthInfo);

  useEffect(() => {
    const access = getAccessType("expense_entry");
    access_type_set(access);
  }, []);
 const {disabledDate} = useMigrationDate();
  // initial load
  useEffect(() => {
          
    if (!id) dispatch(fetchExpenseCode());
    dispatch(fetchHeadAccs());
    dispatch(fetchEmployees());
    dispatch(fetchAccountsByType(["cash_in_hand", "bank_account"]));
    dispatch(fetchTagGroups())
      .unwrap()
      .then((groups) =>
        Promise.all(
          groups.map((g) => dispatch(fetchTagGroupFields(g.id)).unwrap())
        )
      )
      .then((allFields) => {
        const combined = allFields.flat().map((item) => ({
          ...item.raw,
          combinedField: item.display_text,
          id: item.id,
        }));
        setTagGroupList(combined);
      })
      .catch(() => { });
    setRows([
      {
        key: Date.now() + Math.random(),
        to_acc_id: 0,
        acc_name: "",
        amount: 0,
      },
    ]);
  }, [dispatch]);

  // if direct edit route accessed
  useEffect(() => {
    if (id && !selectedExpense) {
      dispatch(fetchExpenses())
        .unwrap()
        .then((list) => {
          const found = list.find((r) => r.exp_id == id);
          if (found) {
            dispatch(setSelectedExpense(found));
            dispatch(setIsEditExpense(true));
          }
        })
        .catch(() => { });
    }
  }, [id, selectedExpense, dispatch]);



  // fill form when edit selected
  useEffect(() => {
    if (isEditExpense && selectedExpense) {
      dispatch(setExpenseCode(selectedExpense.exp_code));
 
      // Expense Code, Date, Narration
      form.setFieldsValue({
        exp_date: dayjs(selectedExpense.created_date),
        description: selectedExpense.narration,
        from_acc_display: selectedExpense.acc_name,
        from_acc: selectedExpense.acc_id,
      });

      setSelectedPayFromAcc({
        // acc_id: selectedExpense.acc_id,
        // acc_name: selectedExpense.acc_name,
        display_text: selectedExpense.acc_name,
      });

      // Tags
      const tagValues = selectedExpense.tag_field_id
        ? [
          {
            display_text: selectedExpense.tag_name, // needs API join or lookup
            value: selectedExpense.tag_field_id,
          },
        ]
        : [];
      setSelectedTags(selectedExpense.tag_field_id);
      form.setFieldsValue({ tags: selectedExpense.tag_field_id });

      // Expense heads (rows)
      setRows(
        (selectedExpense.details || []).map((d) => ({
          key: d.exp_d_id || Date.now() + Math.random(),
          exp_head: {
            display_text: d.acc_name,
            value: d.to_acc_id,
          },
          to_acc_id: d.to_acc_id,
          acc_name: d.acc_name,
          amount: d.exp_amount || 0,
        }))
      );

      // Employee
      if (selectedExpense.employee_id) {
        const foundEmp = employees.find(
          (e) => e.employee_id == selectedExpense.employee_id
        );
        if (foundEmp) {
          const empValue = {
            sale_id: foundEmp.employee_id, // 🔑 must match option.key
            display_text: `${foundEmp.employee_name} - ${foundEmp.employee_code}`,
            raw: foundEmp,
          };
          form.setFieldsValue({
            employee_id: empValue,
          });

          setSelectedEmployeeLocal(foundEmp);
        }
      }

      // Pay From Account
      if (selectedExpense.from_acc_id && accountsByType.length > 0) {
        const foundAcc = accountsByType.find(
          (a) => a.acc_id == selectedExpense.from_acc_id
        );
        if (foundAcc) {
          const accValue = {
            sale_id: foundAcc.acc_id, // 🔑 must match option.key
            display_text: foundAcc.acc_name,
            raw: foundAcc,
          };

          form.setFieldsValue({
            from_acc_display: accValue,
          });
          setSelectedPayFromAcc(foundAcc); // optional
        }
      }

      // Entry Date in slice
      dispatch(setEntryDate(dayjs(selectedExpense.created_date).toISOString()));
    }
  }, [
    isEditExpense,
    selectedExpense,
    employees,
    accountsByType,
    form,
    dispatch,
  ]);

  console.log(selectedEmployeeLocal, "selectedEmployeeLocal123");

  // add/remove rows
  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        key: Date.now() + Math.random(),
        to_acc_id: 0,
        acc_name: "",
        amount: 0,
      },
    ]);
  };
  const handleRemoveRow = (key) => setRows(rows.filter((r) => r.key !== key));

  const handleCancel = () => {
    dispatch(setSelectedExpense(null));
    dispatch(setIsEditExpense(false));
    navigate("/accounts/expenses");
  };

  // Save
  const onFinish = async (values) => {

    // convert selected/entry Date to utc date + time
    let finalEntryDate = entryDate;
  if (!dayjs(entryDate).isSame(dayjs(), "day")) {
      finalEntryDate = getDateTimeFromSelectedDate(entryDate); // Merge date + current time in TZ
    } else {
      finalEntryDate = entryDate; // Use now in TZ
    }

    try {
      const totalAmount = rows.reduce(
        (sum, r) => sum + Number(r.amount || 0),
        0
      );

      if (!selectedPayFromAcc?.acc_id) {
        modal.error({ title: "Select a Pay From Account before saving." });
        return;
      }
      if (!entryDate) {
        modal.error({ title: "Entry Date is required!" });
        return;
      }
      if (rows.length === 0 || totalAmount === 0) {
        modal.error({ title: "Expense rows cannot be empty." });
        return;
      }

      for (let r of rows) {
        if (!r.acc_name) {
          modal.error({ title: "Please select Expense Head before saving." });
          return;
        }
        if (r.amount == null || r.amount <= 0) {
          modal.error({ title: "Please enter a valid Amount before saving." });
          return;
        }
      }
      const payloadExpense = {
        exp_id: isEditExpense ? selectedExpense.exp_id : 0,
        action: isEditExpense ? "update" : "create",
        exp_code: expenseCode,
        // created_date: values.exp_date.toISOString(), // already date picker value
        created_date: formatToUtc(finalEntryDate), // already date picker value
        from_acc_id: selectedPayFromAcc.acc_id,
        employee_id: selectedEmployeeLocal?.employee_id || 0,
        pay_to: selectedEmployeeLocal
          ? `${selectedEmployeeLocal.employee_name} - ${selectedEmployeeLocal.employee_code}`
          : "",
        exp_total: totalAmount,
        narration: values.description || "",
      };

      if (selectedTags.length > 0) {
        payloadExpense.tag_field_id = selectedTags;
      } else {
        payloadExpense.tag_field_id = undefined;
      }

      const payload = {
        expense: payloadExpense,
        expenseDetail: rows.map((r) => ({
          to_acc_id: r.to_acc_id,
          acc_name: r.acc_name,
          exp_amount: Number(r.amount) || 0,
        })),
      };

      const res = await dispatch(saveExpense(payload)).unwrap();
      modal.success({ title: res.msg || "Expense saved successfully" });

      // Reset after save
      dispatch(setSelectedExpense(null));
      dispatch(setIsEditExpense(false));
      dispatch(setRefreshList(true));

      form.resetFields();
      setRows([]);
      navigate("/accounts/expenses");
    } catch (err) {
      modal.error({ title: err.message || "Failed to save expense" });
    }
  };

  // columns for expense rows
  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 50,
    },
    {
      title: "Expense Head",
      dataIndex: "acc_name",
      render: (val, record, index) => (
        <div>
          <SearchListAutoComplete
            entityType="accounts"
            style={{ width: "100%" }}
            optionsList={(headAccs || []).map((acc) => ({
              sale_id: acc.acc_id,
              display_text: acc.acc_name,
              raw: acc,
            }))}
            onSelect={(obj) => {
              const newRows = [...rows];
              newRows[index].to_acc_id = obj?.raw?.acc_id || 0;
              newRows[index].acc_name = obj?.raw?.acc_name || "";
              setRows(newRows);
            }}
            value={
              record.acc_name
                ? {
                  value: record.acc_name,
                  label: record.acc_name,
                }
                : null
            }
            placeholder="Expense Head"
            showAddNew={false}
          />
        </div>
      ),
    },
    {
      title: "Expense Amount",
      dataIndex: "amount",
      width: screens.md ? undefined : 110,
      render: (_, record, index) => (
        <InputNumberField
          min={0}
          data-testid={`exp-amount`}
          value={record.amount}
          step={1} // allow decimal steps
          precision={2} // max 2 decimal places
          style={{ width: "100%" }}
          placeholder="0.00"
          onClick={() => {
            const newRows = [...rows];

            if (newRows[index].amount === 0) {
              newRows[index].amount = "";
              setRows(newRows);
            }
          }}
          onChange={(val) => {
            const newRows = [...rows];
            newRows[index].amount = val || 0;
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Button type="link" danger onClick={() => handleRemoveRow(record.key)} icon={<DeleteOutlined />}/>
      ),
    },
  ];

  const totalAmount = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);

  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>Expense Entry</Title>
      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1400px" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
        initialValues={{ exp_date: dayjs(entryDate) }}
        onFinish={onFinish}
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            {/* Expense Code */}
            <Form.Item label="Expense Code">
              <Input value={expenseCode} disabled />
            </Form.Item>

            {/* Expense Date */}
            <Form.Item
              name="exp_date"
              label="Expense Date"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(d) =>
                  dispatch(setEntryDate(d ? d.toISOString() : null))
                }
                format={dateTimeFormat}
                                disabledDate={disabledDate} 
              />
            </Form.Item>

            {/* Pay From Account */}
            <Form.Item
              name="from_acc_display"
              label="Pay From Account"
              rules={[
                { required: true, message: "Please Select Pay From Account" },
              ]}
            // getValueFromEvent={(obj) => obj?.raw?.acc_id}
            >
              <SearchListAutoComplete
                entityType="accounts"
                style={{ width: "100%" }}
                optionsList={(accountsByType || []).map((acc) => ({
                  sale_id: acc.acc_id,
                  display_text: acc.acc_name,
                  raw: acc,
                }))}
                value={selectedPayFromAcc}
                onSelect={(obj) => {
                  setSelectedPayFromAcc(obj?.raw);
                  form.setFieldValue("from_acc", obj?.raw?.acc_id);
                  // store UI display value so the input shows text
                  form.setFieldValue("from_acc_display", obj?.display_text);
                }}
                placeholder="Cash / Bank"
                showAddNew={false}
              />
            </Form.Item>
            <Form.Item name="from_acc" hidden>
              <Input />
            </Form.Item>
          </Flex>
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            {/* Employee */}
            <Form.Item name="employee_id" label="Employee">
              <SearchListAutoComplete
                entityType="employee"
                style={{ width: "100%" }}
                placeholder="Employee"
                optionsList={(employees || []).map((emp) => ({
                  sale_id: emp.employee_id,
                  display_text: `${emp.employee_name} - ${emp.employee_code}`,
                  raw: emp,
                }))}
                onSelect={(obj) => {
                  setSelectedEmployeeLocal(obj?.raw);
                }}
                showAddNew={false}
              />
            </Form.Item>

            {/* Tags */}
            <Form.Item name="tags" label="Tags">
              <Select
                mode="multiple"
                maxTagCount="responsive"
                className="tag-multiselect"
                allowClear
                style={{ width: "100%" }}
                placeholder="Choose Tag Groups"
                options={tagGroupList.map((item) => ({
                  value: item.id,
                  label: item.combinedField,
                }))}
                onChange={(ids) => setSelectedTags(ids)}
                popupRender={(menu) => (
                  <>
                    {menu}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 8,
                        borderTop: "1px solid #f0f0f0",
                      }}
                    >
                      <Button type="link" onClick={() => setTagModal(true)}>
                        + Add New Tag
                      </Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>
          </Flex>
        </Flex>

        <Button
          type="dashed"
          onClick={handleAddRow}
          style={{ marginBottom: 12 }}
        >
          + Add Row
        </Button>

        <Table
          dataSource={rows}
          columns={columns}
          pagination={false}
          rowKey="key"
          bordered
          style={{ marginTop: 16, marginBottom: 24 }}
          scroll={{ x: "max-content" }}
          footer={() => (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <strong>
                Total Expense Amount: {Number(totalAmount).toFixed(2)}
              </strong>
            </div>
          )}
        />

        <Form.Item name="description" label="Description">
          <TextArea rows={3} />
        </Form.Item>

        <Space style={{ marginTop: 16 }}>
          <Button onClick={handleCancel}>Back</Button>
          <Button
            onClick={() => {
              form.resetFields();
              setRows([]);
            }}
          >
            Reset
          </Button>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                htmlType="submit"
                data-testid="save-expense-btn"
              >
                Save Expense
              </Button>
            </>
          )}
        </Space>
      </Form>
      <AddNewModal
        open={tagModal}
        title="Add Tag"
        onClose={() => setTagModal(false)}
      >
        <TagCreateForm
          authInfo={authInfo}
          onSuccess={async ({ createdName }) => {
            const res = await dispatch(fetchTagGroupFields("")).unwrap();

            const match = res.find(
              (t) =>
                t.raw.field_name.toLowerCase() === createdName.toLowerCase()
            );

            if (!match) {
              message.warning("Tag creation failed or tag not found");
              return;
            }

            // 🔥 UPDATE OPTIONS FIRST
            setTagGroupList((prev) => {
              const exists = prev.some((p) => p.id === match.id);
              return exists
                ? prev
                : [
                  ...prev,
                  {
                    ...match.raw,
                    combinedField: match.display_text,
                    id: match.id,
                  },
                ];
            });

            const current = form.getFieldValue("tags") || [];

            if (current.includes(match.id)) {
              message.info("Tag already exists and is already selected");
              return;
            }

            // ✅ NOW auto-select
            form.setFieldsValue({
              tags: [...current, match.id],
            });

            message.success("Tag added");
            setTagModal(false);
          }}
        />
      </AddNewModal>
    </div>
  );
};

export default ExpenseEntry;
